import { applyToPoint, type Matrix, type Point } from './matrix';

/** Axis-aligned rectangle. Width and height are never negative. */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export const EMPTY_RECT: Rect | null = null;

export function rectFromPoints(points: readonly Point[]): Rect | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function unionRects(rects: readonly (Rect | null | undefined)[]): Rect | null {
  let result: Rect | null = null;
  for (const r of rects) {
    if (!r) continue;
    if (!result) {
      result = r;
      continue;
    }
    const minX = Math.min(result.x, r.x);
    const minY = Math.min(result.y, r.y);
    const maxX = Math.max(result.x + result.width, r.x + r.width);
    const maxY = Math.max(result.y + result.height, r.y + r.height);
    result = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return result;
}

export function inflateRect(r: Rect, amount: number): Rect {
  return {
    x: r.x - amount,
    y: r.y - amount,
    width: r.width + 2 * amount,
    height: r.height + 2 * amount,
  };
}

export function rectCenter(r: Rect): Point {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

export function rectCorners(r: Rect): [Point, Point, Point, Point] {
  return [
    { x: r.x, y: r.y },
    { x: r.x + r.width, y: r.y },
    { x: r.x + r.width, y: r.y + r.height },
    { x: r.x, y: r.y + r.height },
  ];
}

/** Axis-aligned bounds of a rectangle after an affine transform. */
export function transformRect(r: Rect, m: Matrix): Rect {
  return rectFromPoints(rectCorners(r).map((p) => applyToPoint(m, p))) as Rect;
}

export function rectContainsPoint(r: Rect, p: Point, tolerance = 0): boolean {
  return (
    p.x >= r.x - tolerance &&
    p.x <= r.x + r.width + tolerance &&
    p.y >= r.y - tolerance &&
    p.y <= r.y + r.height + tolerance
  );
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.x <= b.x + b.width && b.x <= a.x + a.width && a.y <= b.y + b.height && b.y <= a.y + a.height
  );
}

/** True when `inner` lies entirely within `outer` (inclusive, with tolerance). */
export function rectContainsRect(outer: Rect, inner: Rect, tolerance = 0): boolean {
  return (
    inner.x >= outer.x - tolerance &&
    inner.y >= outer.y - tolerance &&
    inner.x + inner.width <= outer.x + outer.width + tolerance &&
    inner.y + inner.height <= outer.y + outer.height + tolerance
  );
}
